<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="hrissecondary"
        class="white--text px-4 ml-2"
        v-on="on"
      >
        Copy
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Copy Type of Misconduct"
      text="Bulk create type of misconduct"
      class="mb-0"
    > 
      <form>
        <v-container>
          <v-row no-gutters>
            <v-col
              cols="5"
              class="py-0 my-0"
            >
              <span>Create new version? </span>
              <v-radio-group
                v-model="newVersion"
                class="mt-0"
                row
                @change="revision = null; startDate = null"
              >
                <v-radio
                  label="Yes"
                  :value="1"
                />
                <v-radio
                  label="No"
                  :value="0"
                /> 
              </v-radio-group>
            </v-col>
            <v-col
              v-if="!newVersion"
              cols="7"
              class="py-0 my-0"
            >
              <v-autocomplete
                v-model="revision"
                v-validate="'required'"
                :error-messages="errors.collect('revision')"
                data-vv-name="revision"  
                data-vv-as="version"  
                :items="typeOfMisconductRevisions"
                label="Type of Misconduct (Current Version)"
                item-text="name"
                item-value="id"
              />
            </v-col>
            <v-col
              v-else
              cols="7"
              class="py-0 my-0"
            >
              <v-text-field
                v-model="nextVersion"
                label="New Version"
                disabled
              />
            </v-col>
            <v-col
              cols="12"
            >
              <v-autocomplete
                v-model="versionFilter"
                v-validate="'required'"
                :error-messages="errors.collect('versionFilter')"
                data-vv-name="version filter"  
                :items="typeOfMisconductRevisions"
                label="Version to copy"
                item-text="name"
                item-value="id"
                outlined
                @change="loadTypeOfMisconducts"
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="policyFilter"
                v-validate="'required'"
                :error-messages="errors.collect('policyFilter')"
                data-vv-name="group filter" 
                :items="jobGroupPoliciesWithNull"
                label="Job group policy to copy"
                item-text="name"
                item-value="id"
                outlined
                @change="loadTypeOfMisconducts"
              />
            </v-col>
            
            <v-row
              v-if="!loading && versionFilter && policyFilter && !noRecord"
              no-gutters
            >
              <v-col
                v-if="newVersion"
                cols="12"
                class="py-0"
              >
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="startDateDisplay"
                      v-validate="'required'"
                      label="Version Start Date"
                      :error-messages="errors.collect('startDateDisplay')"
                      data-vv-name="startDateDisplay"  
                      data-vv-as="start date"    
                      readonly
                      outlined
                      class="label-position"
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    @input="startDateMenu = false"
                  />
                </v-menu>
              </v-col>
              
              <v-col
                cols="12"
              >
                <v-text-field
                  v-model="code"
                  v-validate="'required|integer|min_value:1'"
                  :error-messages="errors.collect('code')"
                  data-vv-name="code"
                  type="number"
                  label="First New Parent Code Number"
                  outlined
                  :min="1"
                  @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                >
                  <v-tooltip 
                    slot="append"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon
                        color="warning"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>Smallest parent code number based on the selected version and group to copy: <b>{{ smallestParentNumber }}</b> </span>
                  </v-tooltip>
                </v-text-field>
              </v-col>

              <v-col cols="12">
                <v-autocomplete
                  v-model="group"
                  v-validate="'required'"
                  :error-messages="errors.collect('group')"
                  data-vv-name="group" 
                  :items="filteredJobGroupPolicies"
                  label="Job Group Policy"
                  item-text="name"
                  item-value="id"
                  outlined
                />
              </v-col>
            </v-row>
        
            <v-row v-if="!loading && noRecord">
              <v-col
                cols="12"
                class="px-6 pb-2 mt-n3"
              >
                <v-icon
                  class="pr-1"
                  color="red"
                >
                  mdi-information-outline
                </v-icon>
                <span
                  class="red--text"
                >
                  No type of misconducts record found. Please select another filter.
                </span>
              </v-col>
            </v-row>
          </v-row>
        </v-container>
      </form>
      
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"  
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          :disabled="loading || noRecord"
          color="hrissecondary" 
          class="white--text px-10"        
          @click="submitForm"
        >
          Create
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      versionStarter: {
        type: String,
        default: '',
      },
      jobGroupPolicies: {
        type: Array,
        default: null,
      },
      typeOfMisconductRevisions: {
        type: Array,
        default: null,
      },
    },
    data () {
      return {
        loading: false,
        progressing: false,
        dialog: null,
        versionFilter: null,
        policyFilter: null,
        code: null,
        revision: null,
        group: null,
        startDateMenu: false,
        startDate: null,
        noRecord: false,
        newVersion: 1,
        smallestParentNumber: 0,
        typeOfMisconducts: [],
        nextVersion: '',
      }
    },
    computed: {
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      jobGroupPoliciesWithNull () {
        let temp = [ ...this.jobGroupPolicies ]

        temp.push({ id: 'null', name: 'N/A' })

        return temp
      },
      filteredJobGroupPolicies () {
        let temp = this.jobGroupPoliciesWithNull

        if (!this.newVersion && this.revision && this.policyFilter) {
          temp = temp.filter(group => { 
            return group.id !== this.policyFilter 
          })
        }

        return temp
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return this.$resetData()

        const latestVer = this.typeOfMisconductRevisions[0].version
        const nextVer = (Number(latestVer) + 1).toString()
        this.nextVersion = this.versionStarter + nextVer.padStart(latestVer.length, '0')
      },
    },
    methods: {
      loadTypeOfMisconducts () {
        if (this.versionFilter && this.policyFilter) {
          this.loading = true
          this.$api.typeOfMisconducts().list({ version_id: this.versionFilter, group_id: this.policyFilter })
            .then(res => {
              this.typeOfMisconducts = _.orderBy(res.data, ['code'], ['asc'])

              if (this.typeOfMisconducts.length <= 0) {
                this.noRecord = true
              } else {
                let parentNumbers = []
                
                this.typeOfMisconducts.map(item => {
                  const code = parseInt(item.code.split('.', 1))
                  
                  parentNumbers.push(code)
                })
                parentNumbers.sort((a, b) => a - b)

                parentNumbers = [...new Set(parentNumbers)]

                this.smallestParentNumber = parentNumbers[0]
                this.noRecord = false
              }

              this.loading = false
            })
        }
      },
      prepareFormData () {
        return {
          version_filter: this.versionFilter,
          policy_filter: this.policyFilter === 'null' ? null : this.policyFilter,
          start_date: this.startDate,
          difference: this.code - this.smallestParentNumber,
          job_group_policy_id: (this.group === 'undefined' || this.group === 'null') ? null : this.group,
          type_of_misconduct_revision_id: this.revision,
        }
      },   
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true
    
            this.$api.typeOfMisconducts().createMany(this.prepareFormData())
              .then((res) => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('created', res.data)                
                  this.$snack({ message: res.data.status })

                  this.$validator.reset()
                  this.dialog = false               
                }
              })
              .catch(() => {             
                this.$snack({ 
                  color: 'error',
                  message: 'Unable to copy the type of misconducts', 
                })        
            
                this.progressing = false
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
  
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px
}
</style>
